import styles from "./StepThree.module.css";
import Icon from "../../../components/Icon/Icon";
import { useEffect, useMemo, useState } from "react";

import CreatePropertyModal from "../../../components/CreatePropertyModal/CreatePropertyModal";
import EditPropertyModal from "../../../components/EditPropertyModal/EditPropertyModal";
import type {
  GetTemplateResultSuccessType,
  IExtractDataFormElement,
} from "../../../shared/types/files.types";
import type {
  ICreateTemplateFieldsCredentials,
  IGetTemplateByIdResponse,
  IUpdateTemplateFieldsCredentials,
} from "../../../shared/types/templates.types";
import ConfirmActionModal from "../../../components/ConfirmActionModal/ConfirmActionModal";
import StatusModal from "../../../components/StatusModal/StatusModal";
import Tooltip from "../../../components/Tooltip/Tooltip";

type StepThreeProps = {
  onSubmit: (fields: IUpdateTemplateFieldsCredentials[]) => void;
  extractedData: GetTemplateResultSuccessType;
  getBack: () => void;
  savedData: IGetTemplateByIdResponse["fields"];
  onTestQueries: (queries: { field: string; question: string }[]) => void;
};

type FieldType = IUpdateTemplateFieldsCredentials & {
  confidence: number;
  sampleValue: string;
  isPredefined?: boolean;
  isSavedDataField?: boolean;
};

type FieldsType = FieldType[];

const toCamelCase = (label: string) => {
  return label
    .trim()
    .split(" ")
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join("");
};

const StepThree = ({
  onSubmit,
  extractedData,
  getBack,
  savedData,
  onTestQueries,
}: StepThreeProps) => {
  const uniqueExtractedForms = useMemo(() => {
    const formMap = new Map<string, IExtractDataFormElement>();
    extractedData.forms.forEach((form) => {
      const baseKeyword = form.key.replace(/:.*$/, "").toLowerCase();
      const existingForm = formMap.get(baseKeyword);

      if (!existingForm || form.confidence > existingForm.confidence) {
        formMap.set(baseKeyword, form);
      }
    });
    return Array.from(formMap.values());
  }, [extractedData.forms]);

  const mappedSavedFields: FieldsType = savedData.map((field) => ({
    id: field.id,
    name: field.name,
    label: field.label,
    deletedAt: field.deletedAt,
    order: 1,
    isRequired: true,
    isSavedDataField: true,
    keywords: field.keywords
      ? field.keywords.map((keywordObj) => {
          return { keyword: keywordObj.keyword };
        })
      : [],
    queries: field.queries
      ? field.queries.map((q) => ({
          question: q.question,
        }))
      : [],
    confidence: 100,
    sampleValue: "Los campos creados manualmente no tienen valores",
  }));

  const [typeFields, setTypeFields] = useState<FieldsType>(mappedSavedFields);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [
    isConfirmDeletePropertyModalOpen,
    setIsConfirmDeletePropertyModalOpen,
  ] = useState(false);

  const [isSuccessDeleteModalOpen, setIsSuccessDeleteModalOpen] =
    useState(false);

  const [deletedFieldsNames, setDeletedFieldsNames] = useState<string[]>([]);

  const [fieldToEdit, setFieldToEdit] = useState<FieldType | null>(null);

  const handleCreateProperty = ({
    label,
    keywords,
  }: {
    label: string;
    keywords: { keyword: string }[];
  }) => {
    let matchedSampleValue = "Valor no encontrado";
    let matchedConfidence = 0;
    const foundMatch = uniqueExtractedForms.find((form) =>
      keywords.some((keywordObj) =>
        form.key.toLowerCase().includes(keywordObj.keyword.toLowerCase())
      )
    );
    if (foundMatch) {
      matchedSampleValue =
        foundMatch.sampleValue === ""
          ? "Valor no encontrado"
          : foundMatch.sampleValue;
      matchedConfidence = Math.floor(foundMatch.confidence);
    }
    setTypeFields((prev) => [
      {
        name: toCamelCase(label),
        label: label,
        keywords,
        isRequired: true,
        order: prev.length + 1,
        sampleValue: matchedSampleValue,
        confidence: matchedConfidence,
        deletedAt: null,
      },
      ...prev,
    ]);
    setIsCreateModalOpen(false);
  };

  const handleEditProperty = (updatedField: FieldType) => {
    const newName = toCamelCase(updatedField.label);

    let updatedSampleValue = "Valor no encontrado";
    let updatedConfidence = 0;
    const foundMatch = uniqueExtractedForms.find(
      (form) =>
        updatedField.keywords &&
        updatedField.keywords.some((keywordObj) =>
          form.key.toLowerCase().includes(keywordObj.keyword.toLowerCase())
        )
    );
    if (foundMatch) {
      updatedSampleValue =
        foundMatch.sampleValue === ""
          ? "Valor no encontrado"
          : foundMatch.sampleValue;
      updatedConfidence = Math.floor(foundMatch.confidence);
    }

    setTypeFields((prevFields) =>
      prevFields.map((field) => {
        if (field.name === updatedField.name) {
          return {
            ...updatedField,
            name: newName,
            sampleValue: updatedSampleValue,
            confidence: updatedConfidence,
          };
        }
        return field;
      })
    );
    setIsEditModalOpen(false);
    setFieldToEdit(null);
  };

  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const handleCheckboxChange = (key: string) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleDeleteSelected = () => {
    setTypeFields((prev) =>
      prev.filter((field) => !selectedFields.includes(toCamelCase(field.label)))
    );
    setDeletedFieldsNames(selectedFields);
    setSelectedFields([]);
  };

  const existingFieldsNames = useMemo(() => {
    if (isEditModalOpen && fieldToEdit) {
      return typeFields
        .filter((field) => field.name !== fieldToEdit.name)
        .map((field) => field.label);
    }
    return typeFields.map((field) => field.label);
  }, [typeFields, isEditModalOpen, fieldToEdit]);

  const existingKeywords = uniqueExtractedForms.flatMap((form) =>
    form.key.replace(/:.*$/, "").toLowerCase()
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fieldsToSend: ICreateTemplateFieldsCredentials[] = typeFields.map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ confidence, sampleValue, isSavedDataField, ...field }, index) => {
        return {
          ...field,
          order: index + 1,
        };
      }
    );

    onSubmit(fieldsToSend);
  };

  useEffect(() => {
    console.log(typeFields);
  }, [typeFields]);

  const handleRestoreField = (label: string) => {
    setTypeFields((prevFields) =>
      prevFields.map((field) =>
        field.label === label ? { ...field, deletedAt: null } : field
      )
    );
  };

  // --- Update all fields' sampleValue after entering Step 3 or when keywords/extracted data change ---
  useEffect(() => {
    setTypeFields((prevFields) =>
      prevFields.map((field) => {
        let matchedSampleValue = "Valor no encontrado";
        let matchedConfidence = 0;
        // Find a match for any keyword
        const foundMatch =
          field.keywords && field.keywords.length > 0
            ? uniqueExtractedForms.find(
                (form) =>
                  field.keywords &&
                  field.keywords.some((keywordObj) =>
                    form.key
                      .toLowerCase()
                      .includes(keywordObj.keyword.toLowerCase())
                  )
              )
            : undefined;
        if (foundMatch) {
          matchedSampleValue =
            foundMatch.sampleValue === ""
              ? "Valor no encontrado"
              : foundMatch.sampleValue;
          matchedConfidence = Math.floor(foundMatch.confidence);
        }
        return {
          ...field,
          sampleValue: matchedSampleValue,
          confidence: matchedConfidence,
        };
      })
    );
  }, [uniqueExtractedForms, savedData]);

  // --- Test Queries Button Logic ---
  const handleTestQueries = () => {
    const queries = typeFields.flatMap((field) =>
      (field.queries ?? []).map((query) => ({
        field: field.name,
        question: query.question,
      }))
    );
    onTestQueries(queries);
  };

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.container}>
          <h2 className={styles.title}>Propiedades extraídos</h2>
          <div className={styles.buttonsContainer}>
            <button
              className={styles.createButton}
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Crear propiedad
              <Icon size={22} name="edit-pen" gradient={true} />
            </button>
            <span className={styles.divider}></span>
            <button
              onClick={() => {
                setIsConfirmDeletePropertyModalOpen(true);
              }}
              className={styles.deleteButton}
              type="button"
              disabled={selectedFields.length === 0}
            >
              Eliminar propiedades
              <Icon name="trash-can" size={18} color="#FFFFFF" />
            </button>
          </div>
          <ul className={styles.propertiesList}>
            {typeFields.map((field) => (
              <li
                className={`${styles.propertiesListItem} ${
                  field.deletedAt !== null && styles.deprecated
                }`}
                key={field.name}
              >
                <div className={styles.propertiesActionsContainer}>
                  {field.deletedAt !== null && (
                    <button
                      type="button"
                      className={styles.restoreButton}
                      onClick={() => {
                        handleRestoreField(field.label);
                      }}
                    >
                      <Icon name="document-verify" size={28} />
                    </button>
                  )}
                  {field.isSavedDataField && (
                    <Tooltip
                      content="Usted mismo creó este campo anteriormente,
                       por lo que es 100 % preciso y no tiene ningún valor de ejemplo."
                      wrapperClassName={styles.tooltipWrapper}
                    >
                      <p className={styles.tooltipContent}>i</p>
                    </Tooltip>
                  )}
                  <button
                    className={styles.editFieldButton}
                    type="button"
                    onClick={() => {
                      setFieldToEdit(field);
                      setIsEditModalOpen(true);
                    }}
                  >
                    <Icon name="edit-pen" size={24} />
                  </button>
                </div>
                <div className={`${styles.propertyContent} `}>
                  <p className={styles.propertyName} title={field.label}>
                    {field.label}
                  </p>

                  {/* Show keywords if any, with sample value in parentheses only for user-inputted keywords */}
                  {field.keywords && field.keywords.length > 0 && (
                    <ul className={styles.keywordsList}>
                      {field.keywords.map((keywordObj, i) => {
                        return (
                          <li
                            key={i}
                            className={styles.keywordsListItem}
                            title={keywordObj.keyword}
                          >
                            <span className={styles.keywordTextEllipsis}>
                              {" "}
                              {keywordObj.keyword}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* Show queries if any */}
                  {field.queries && field.queries.length > 0 && (
                    <ul
                      className={styles.keywordsList}
                      style={{ marginTop: 4 }}
                    >
                      {field.queries.map((q, i) => (
                        <li key={i} className={styles.keywordsListItem}>
                          <span>{q.question}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className={styles.inputContainer}>
                    <input
                      className={styles.customCheckbox}
                      onChange={() => handleCheckboxChange(field.name)}
                      checked={selectedFields.includes(field.name)}
                      type="checkbox"
                    />
                    <input
                      className={styles.propertyData}
                      value={field.sampleValue}
                      readOnly
                    />
                    <span className={styles.confidence}>
                      {`(${Math.floor(field.confidence)}% precisión)`}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.formActionsButtonsContainer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={getBack}
          >
            Volver
          </button>
          {/* --- Add Test Queries Button --- */}
          <button
            type="button"
            className={styles.submitButton}
            style={{ marginRight: 8 }}
            onClick={handleTestQueries}
          >
            Test Queries
          </button>
          <button className={styles.submitButton} type="submit">
            Actualizar Documento
          </button>
        </div>
      </form>

      <CreatePropertyModal
        isOpen={isCreateModalOpen}
        onRequestClose={() => setIsCreateModalOpen(false)}
        existingFieldsNames={existingFieldsNames}
        existingKeywords={existingKeywords}
        onCreate={handleCreateProperty}
      />
      {fieldToEdit && (
        <EditPropertyModal
          isOpen={isEditModalOpen}
          onRequestClose={() => {
            setIsEditModalOpen(false);
            setFieldToEdit(null);
          }}
          existingFieldsNames={existingFieldsNames}
          existingKeywords={existingKeywords}
          fieldToEdit={{
            ...fieldToEdit,
            keywords:
              fieldToEdit.keywords && Array.isArray(fieldToEdit.keywords)
                ? fieldToEdit.keywords.map((k) => ({ keyword: k.keyword }))
                : [],
            queries:
              fieldToEdit.queries && Array.isArray(fieldToEdit.queries)
                ? fieldToEdit.queries.map((q) => ({ question: q.question }))
                : [],
          }}
          onEdit={handleEditProperty}
        />
      )}
      <ConfirmActionModal
        isOpen={isConfirmDeletePropertyModalOpen}
        onRequestClose={() => {
          setIsConfirmDeletePropertyModalOpen(false);
        }}
        modalTitle="Eliminar Propiedad"
        modalText="¿Está seguro que desea eliminar esta
         propiedad?, no se extraerá esta propiedad para futuros documentos"
        cancelActionText="Cancelar"
        cancelActionHandler={() => {
          setIsConfirmDeletePropertyModalOpen(false);
        }}
        confirmActionText="Eliminar propiedad"
        confirmActionHandler={() => {
          handleDeleteSelected();
          setIsConfirmDeletePropertyModalOpen(false);
          setIsSuccessDeleteModalOpen(true);
        }}
      />
      <StatusModal
        icon={
          <Icon
            name="folder-with-trashcan"
            gradient={true}
            className={styles.modalIcon}
            size={110}
          />
        }
        isOpen={isSuccessDeleteModalOpen}
        onRequestClose={() => {
          setIsSuccessDeleteModalOpen(false);
          setDeletedFieldsNames([]);
        }}
      >
        <p className={styles.statusModalText}>
          La propiedad {deletedFieldsNames.map((field) => field).join(", ")} se
          ha eliminado
        </p>
      </StatusModal>
    </>
  );
};

export default StepThree;
